import { useState, useEffect } from 'react';
import { Grid } from '@mui/material';

import SpectrumChart from 'features/spectrumChart/SpectrumChart';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { InterferenceDetector } from 'types/shared/InterferenceDetector';
import { selectedMarkerChanged } from 'features/markers/markersSlice';
import useInterval from '../../hooks/useInterval';

import { TraceHistory } from '../../types';

import { updateTraceData } from '../spectrasWebSocketServer/spectrasWebSocketServerSlice';

import TraceHistoryPlaybackControls from './TraceHistoryPlaybackControls';
import useHighlightSliderAlarms from './hooks/useHighlightSliderAlarms';
import { PlayStatus, SavedTraceHistoryIds } from './types';

import { parseTraceFromBuffer } from 'utils/traceUtils';

import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { formatInTimeZone } from 'date-fns-tz';

interface Props {
  filter: SavedTraceHistoryIds;
  traceHistory: TraceHistory[];
  traceCount: number;
  traceIndex: number;
  onHover?: () => void;
  handleTraceIndexChanged: (newValue: any) => void;
  handleChartClicked?: () => void;
  hideDetectorNames?: boolean;
  onDrawingModeChange?: (newDrawingMode: any) => void;
}

const TraceHistoryPlayBack = ({
  filter,
  traceHistory,
  traceCount,
  traceIndex,
  onHover,
  handleTraceIndexChanged,
  handleChartClicked,
  hideDetectorNames,
  onDrawingModeChange,
}: Props) => {
  const dispatch = useAppDispatch();
  const { snapshotId, analyzerId } = filter;
  const { dateFormatWithZeros, timeFormat, timezone } = useUserDateTime();

  const [parsedTrace, setParsedTrace] = useState<number[]>([]);
  const [playTimerStatus, setPlayTimerStatus] = useState<PlayStatus>(
    PlayStatus.ForwardPaused
  );
  const [playTimerInterval, setPlayTimerInterval] = useState(1000);
  const sliderMarks = useHighlightSliderAlarms(snapshotId, traceHistory);

  const [drawingMode, setDrawingMode] = useState<{
    isDrawing: boolean;
    drawingType: InterferenceDetector;
    redrawId: number;
    name?: string | undefined;
  }>({
    isDrawing: false,
    drawingType: InterferenceDetector.Monitor,
    redrawId: -1,
    name: undefined,
  });

  const selectedMarkerIndex = useAppSelector(
    (state) => state.markers.selectedMarker
  );

  useEffect(() => {
    if (selectedMarkerIndex > -1 && selectedMarkerIndex < 104) {
      setDrawingMode({
        isDrawing: true,
        drawingType: InterferenceDetector.Marker,
        redrawId: -1,
      });
    } else {
      setDrawingMode((prevState) => ({
        ...prevState,
        isDrawing: false,
      }));
    }
  }, [selectedMarkerIndex]);

  const handleDrawingFinished = () => {
    setDrawingMode({ ...drawingMode, isDrawing: false, redrawId: -1 });
    dispatch(selectedMarkerChanged(-1));
  };

  if (onDrawingModeChange) {
    onDrawingModeChange(drawingMode);
  }

  useEffect(() => {
    if (traceCount <= 0) return;
    if (traceIndex < 0 || traceIndex > traceCount - 1) return;
    const selectedTrace = traceHistory[0];
    const parsedTrace = parseTraceFromBuffer(selectedTrace?.trace);

    setParsedTrace(parsedTrace);
    dispatch(
      updateTraceData({
        trace: parsedTrace,
        snapshot_id: selectedTrace.snapshot_id,
      })
    );
    //eslint-disable-next-line
  }, [filter, traceIndex, dispatch]);

  const handleTraceIndexChange = (event: any, newValue: number | number[]) => {
    if (typeof newValue === 'number') handleTraceIndexChanged(newValue);
  };

  const handlePlayClicked = () => {
    if (
      playTimerStatus === PlayStatus.ForwardPaused &&
      traceHistory &&
      traceIndex < traceCount - 1
    )
      setPlayTimerStatus(PlayStatus.Forward);
    if (
      playTimerStatus === PlayStatus.BackwardPaused &&
      traceHistory &&
      traceIndex > 0
    )
      setPlayTimerStatus(PlayStatus.Backward);
  };

  const handlePauseClicked = () => {
    setPlayTimerStatus(
      playTimerStatus === PlayStatus.Forward
        ? PlayStatus.ForwardPaused
        : PlayStatus.BackwardPaused
    );
  };

  const handleForwardClicked = () => {
    if (traceHistory && traceIndex < traceCount - 1) {
      handleTraceIndexChanged(traceIndex + 1);
      setPlayTimerStatus(
        playTimerStatus === PlayStatus.Forward ||
          playTimerStatus === PlayStatus.ForwardPaused
          ? PlayStatus.ForwardPaused
          : PlayStatus.BackwardPaused
      );
    } else return;
  };
  const handleBackwardClicked = () => {
    if (traceIndex > 0) {
      handleTraceIndexChanged(traceIndex - 1);
      setPlayTimerStatus(
        playTimerStatus === PlayStatus.Forward ||
          playTimerStatus === PlayStatus.ForwardPaused
          ? PlayStatus.ForwardPaused
          : PlayStatus.BackwardPaused
      );
    } else return;
  };

  const onChartClicked = () => {
    if (handleChartClicked) handleChartClicked();
  };

  const handleReverseClicked = () => {
    if (playTimerStatus === PlayStatus.Forward)
      setPlayTimerStatus(PlayStatus.Backward);
    else if (playTimerStatus === PlayStatus.ForwardPaused)
      setPlayTimerStatus(PlayStatus.BackwardPaused);
    else if (playTimerStatus === PlayStatus.BackwardPaused)
      setPlayTimerStatus(PlayStatus.ForwardPaused);
    else setPlayTimerStatus(PlayStatus.Forward);
  };

  const handleJumpToNextAlarm = () => {
    if (sliderMarks.length <= 0) return;

    const sliderIndexes = sliderMarks.map((mark) => mark.value);
    let indexToJumpTo = -1;

    for (let i = 0; i < sliderIndexes.length; i++) {
      if (traceIndex < sliderIndexes[i]) {
        indexToJumpTo = sliderIndexes[i];
        break;
      }
    }

    if (indexToJumpTo > -1) {
      handleTraceIndexChanged(indexToJumpTo);
    } else {
      handleTraceIndexChanged(sliderIndexes[0]);
    }
  };

  useInterval(
    () => {
      if (playTimerStatus === PlayStatus.Forward && traceIndex < traceCount - 1)
        handleTraceIndexChanged(traceIndex + 1);
      else if (playTimerStatus === PlayStatus.Backward && traceIndex > 0)
        handleTraceIndexChanged(traceIndex - 1);
    },
    playTimerStatus !== PlayStatus.ForwardPaused &&
      playTimerStatus !== PlayStatus.BackwardPaused
      ? playTimerInterval
      : null
  );

  return (
    <div onMouseOver={onHover} style={{ height: '100%', maxWidth: '100%' }}>
      {traceCount > 0 && (
        <Grid
          container
          direction='column'
          wrap='nowrap'
          style={{ height: '100%', maxWidth: '100%' }}
        >
          <Grid item xs={9} onClick={onChartClicked}>
            <SpectrumChart
              analyzerId={analyzerId}
              snapshotId={snapshotId}
              drawingMode={drawingMode}
              drawingFinished={handleDrawingFinished}
              staticSnapshot={traceHistory[0]}
              staticTrace={parsedTrace}
              hideDetectorNames={hideDetectorNames}
            />
          </Grid>

          <Grid container item justifyContent='center'>
            <span>
              {formatInTimeZone(
                new Date(traceHistory[0].time),
                timezone,
                `${dateFormatWithZeros} ${timeFormat}`
              )}
            </span>
          </Grid>
          <Grid container item justifyContent='center' alignContent='center'>
            <TraceHistoryPlaybackControls
              selectedTraceIndex={traceIndex}
              handleTraceIndexChange={handleTraceIndexChange}
              traceHistoryLength={traceCount}
              handleBackwardClicked={handleBackwardClicked}
              handlePlayClicked={handlePlayClicked}
              handleForwardClicked={handleForwardClicked}
              handlePauseClicked={handlePauseClicked}
              handleJumpToNextAlarm={handleJumpToNextAlarm}
              handleReverseClicked={handleReverseClicked}
              sliderMarks={sliderMarks}
              playTimerStatus={playTimerStatus}
              playTimerInterval={playTimerInterval}
              setPlayTimerInterval={setPlayTimerInterval}
            />
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default TraceHistoryPlayBack;
